import { useState } from "react";
import { App as AntdApp, Button, Form, Input, Modal, Space, Table, Tag, Typography, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusOutlined } from "@ant-design/icons";
import type {
  AddSuppressionRequest,
  SuppressionEntry,
  SuppressionsListResponse,
} from "@coco-mailrelay/shared-types";
import { apiRequest } from "../../lib/apiRequest";
import { errorMessage } from "../../lib/errors";

const { Text } = Typography;

const REASON_COLOR: Record<SuppressionEntry["reason"], string> = {
  hard_bounce: "volcano",
  complaint: "magenta",
  manual: "blue",
};

/** Per-tenant suppression list + manual add/delete (docs/305 §3.7). Session auth
 * only (not a step-up class). Manual add only — auto reasons come from webhooks. */
export default function SuppressionsTab({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const { modal } = AntdApp.useApp();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<{ email: string; note?: string }>();

  const listQuery = useQuery({
    queryKey: ["suppressions", tenantId],
    queryFn: () => apiRequest<SuppressionsListResponse>(`/tenants/${tenantId}/suppressions`),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["suppressions", tenantId] });
    void queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
  };

  const addMutation = useMutation({
    mutationFn: (body: AddSuppressionRequest) =>
      apiRequest<SuppressionEntry>(`/tenants/${tenantId}/suppressions`, { method: "POST", body }),
    onSuccess: () => {
      message.success("Suppression added");
      setOpen(false);
      form.resetFields();
      invalidate();
    },
    onError: (err) => message.error(errorMessage(err, "Failed to add suppression")),
  });

  const remove = (entry: SuppressionEntry): void => {
    modal.confirm({
      title: "Remove suppression?",
      content: `Unblock ${entry.email}?`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiRequest(`/tenants/${tenantId}/suppressions/${encodeURIComponent(entry.email)}`, {
            method: "DELETE",
          });
          message.success("Suppression removed");
          invalidate();
        } catch (err) {
          message.error(errorMessage(err, "Failed to remove suppression"));
        }
      },
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
        Add suppression
      </Button>

      <Table<SuppressionEntry>
        rowKey="email"
        loading={listQuery.isLoading}
        dataSource={listQuery.data?.items ?? []}
        pagination={false}
        columns={[
          { title: "Email", dataIndex: "email", key: "email" },
          {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            render: (r: SuppressionEntry["reason"]) => <Tag color={REASON_COLOR[r]}>{r}</Tag>,
          },
          { title: "Note", dataIndex: "note", key: "note", render: (n: string | null) => n ?? "—" },
          {
            title: "Created",
            dataIndex: "created_at",
            key: "created_at",
            render: (v: string) => new Date(v).toLocaleString(),
          },
          {
            title: "Actions",
            key: "actions",
            render: (_, entry) => (
              <Button size="small" danger onClick={() => remove(entry)}>
                Remove
              </Button>
            ),
          },
        ]}
      />
      {listQuery.data ? (
        <Text type="secondary">Total: {listQuery.data.total}</Text>
      ) : null}

      <Modal
        title="Add manual suppression"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        okText="Add"
        confirmLoading={addMutation.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => addMutation.mutate({ email: v.email, reason: "manual", note: v.note ?? null })}
          preserve={false}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input placeholder="blocked@example.com" />
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input.TextArea rows={2} maxLength={2000} placeholder="Optional context" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
